// HomePage.jsx — Personalized AI home page. Different briefing per user, based on
// their role, bio, recent activity, and current business context.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { showToast } from "./lib/theme";
import MyTasks from "./MyTasks";
import HomeFold from "./HomeFold";
import HealthKpiBar from "./HealthKpiBar";
import MyEmployeeHome from "./MyEmployeeHome";
import IntegrationHealthCard from "./IntegrationHealthCard";
import { formatKSADate, formatKSADateTime, ksaHour } from "./lib/ksaTime";
// Roles that see the executive/management dashboard. Everyone else (baristas,
// custom roles, viewer-employees) gets the personal employee home — their
// training, badges, and daily challenge only — no financial data.
const MGMT_ROLES = new Set([
  "admin", "operations", "marketing", "finance",
  "employees_manager", "branch_manager", "ceo", "accountant", "viewer",
]);

// localStorage key for last-known home payload — keyed by user.id so
// switching accounts doesn't expose the wrong dashboard.
const HOME_CACHE_KEY = (uid) => `tw_home_cache_v1:${uid || "anon"}`;
// Soft expiry — anything older than 24h is discarded.
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const ROLE_THEME = {
  admin:      { hue: "linear-gradient(135deg,#6B4E3D 0%,#C2A97F 100%)", emoji: "👑" },
  marketing:  { hue: "linear-gradient(135deg,#EC4899 0%,#F59E0B 100%)", emoji: "🛍" },
  operations: { hue: "linear-gradient(135deg,#14B8A6 0%,#3B82F6 100%)", emoji: "⚙" },
  viewer:     { hue: "linear-gradient(135deg,#6366F1 0%,#8B5CF6 100%)", emoji: "👁" },
};

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }

function timeOfDayGreeting() {
  const h = ksaHour();
  if (h < 5)  return "ليلة سعيدة";
  if (h < 12) return "صباح الخير";
  if (h < 17) return "مساء النور";
  if (h < 21) return "مساء الخير";
  return "أمسية طيبة";
}

export default function HomePage({ onNavigate, renderRoleDash }) {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const roleDash = renderRoleDash ? renderRoleDash(role) : null;

  // Non-management roles WITHOUT a role dashboard (baristas, custom roles)
  // see their personal employee home only.
  if (user && !MGMT_ROLES.has(role) && !roleDash) {
    return <MyEmployeeHome onNavigate={onNavigate} />;
  }

  return <ManagementHome onNavigate={onNavigate} roleDash={roleDash}
                          role={role} renderRoleDash={renderRoleDash} />;
}

// v119.195 — roles whose primary workspace IS their dashboard land on
// «لوحتي» by default; everyone else lands on the AI overview.
const DASH_DEFAULT_ROLES = new Set(["ceo", "branch_manager", "advisor", "consultant"]);
const ADMIN_DASH_OPTIONS = [
  { key: "ceo",            label: "🏛️ المدير التنفيذي" },
  { key: "operations",     label: "🎯 مدير التشغيل" },
  { key: "marketing",      label: "🚀 مدير التسويق" },
  { key: "advisor",        label: "🎯 المستشار" },
  { key: "branch_manager", label: "🏪 مدير الفرع" },
];
const ROLE_DASH_LABEL = {
  ceo: "🏛️ المدير التنفيذي", operations: "🎯 مدير التشغيل",
  marketing: "🚀 مدير التسويق", advisor: "🎯 المستشار",
  consultant: "🎯 المستشار", branch_manager: "🏪 مدير الفرع",
};

function ManagementHome({ onNavigate, roleDash, role, renderRoleDash }) {
  const { user } = useAuth();
  const isAdmin = role === "admin";
  const hasDash = isAdmin || Boolean(roleDash);
  const [view, setView] = useState(() =>
    DASH_DEFAULT_ROLES.has(role) ? "dash" : "overview");
  const [adminDash, setAdminDash] = useState("ceo");
  const [data, setData] = useState(null);
  // `loading` is true ONLY for the first paint when there's no cache.
  // Once we have *any* data (cached or fresh), we render the dashboard
  // and a small refresh indicator instead of a full-screen spinner.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [error, setError] = useState("");
  const didInitialLoad = useRef(false);

  // ⚡ Synchronously hydrate from localStorage BEFORE the first render
  // so the dashboard paints instantly on mobile/PWA re-opens.
  useEffect(() => {
    if (!user?.id || didInitialLoad.current) return;
    didInitialLoad.current = true;
    try {
      const raw = localStorage.getItem(HOME_CACHE_KEY(user.id));
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed?.ts || 0);
        if (parsed?.data && age < CACHE_MAX_AGE_MS) {
          setData(parsed.data);
          setLoading(false);    // unblock UI — dashboard renders from cache
          setRefreshing(true);  // tiny "refreshing" badge while we fetch fresh
        }
      }
    } catch (_e) { /* corrupted cache → ignore, fall back to spinner */ }
  }, [user?.id]);

  const persistCache = useCallback((d) => {
    if (!user?.id || !d) return;
    try {
      localStorage.setItem(HOME_CACHE_KEY(user.id), JSON.stringify({ data: d, ts: Date.now() }));
    } catch (_e) { /* quota exceeded → silently ignore */ }
  }, [user?.id]);

  // Two-phase loader: /fast paints the dashboard quickly (no Claude call),
  // then a follow-up call to the full endpoint refreshes the AI brief in
  // the background. Manual refresh hits the full endpoint with ?refresh=true.
  const load = useCallback(async (refresh = false) => {
    setError("");
    if (refresh) {
      setRefreshing(true);
      setBriefLoading(true);
      try {
        const { data: d } = await api.get(`/ai/personal-home?refresh=true`);
        setData(d);
        persistCache(d);
      } catch (e) {
        setError(formatErr(e.response?.data?.detail) || e.message);
      } finally { setRefreshing(false); setBriefLoading(false); }
      return;
    }
    // First/normal load: /fast first (instant), then background AI brief.
    setRefreshing(true);
    let gotFast = false;
    try {
      const { data: fast } = await api.get(`/ai/personal-home/fast`);
      setData(fast);
      persistCache(fast);
      gotFast = true;
      setLoading(false);
    } catch (e) {
      // Don't show full-screen error if we already have *any* state to
      // paint (cached or partially loaded) — error toasts handle this.
      setError((prev) => prev || formatErr(e.response?.data?.detail) || e.message);
    } finally { setRefreshing(false); }

    // Background: fetch the AI-augmented version (with brief). This call
    // may take 5-15s on first run but never blocks the visible dashboard.
    if (gotFast) {
      setBriefLoading(true);
      try {
        const { data: full } = await api.get(`/ai/personal-home?stale_ok=true`);
        setData(full);
        persistCache(full);
      } catch (_e) { /* keep the /fast snapshot if AI refresh fails */ }
      finally { setBriefLoading(false); }
    }
  }, [persistCache]);

  useEffect(() => { if (user?.id) load(); }, [user?.id, load]);

  // v164 — auto-refresh: every 1 min + when the app regains focus.
  useEffect(() => {
    if (!user?.id) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(true); }, 60 * 1000);
    const onVis = () => { if (!document.hidden) load(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [user?.id, load]);

  // Full-screen spinner only on the very first visit when no cache exists.
  if (loading && !data) {
    return (
      <div data-testid="home-page" style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>
        <div className="card" style={{ padding: 30, textAlign: "center" }}>
          ⏳ تجهيز لوحتك الشخصية...
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 6 }}>
            (المرة الأولى تأخذ ثوانٍ معدودة — المرات القادمة فورية)
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div data-testid="home-page" style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>
        <div className="card" style={{ padding: 20, color: "var(--danger)" }}>
          ⚠ {error}
          <button type="button" className="btn btn-soft" onClick={() => load(true)} style={{ marginInlineStart: 8 }}>إعادة المحاولة</button>
        </div>
      </div>
    );
  }

  const p = data?.profile || {};
  const b = data?.business || {};
  const bh = data?.behavior || {};
  const theme = ROLE_THEME[p.role] || ROLE_THEME.viewer;

  // ── RBAC visibility flags (Feb 2026) ──
  // Sensitive monetary stats are hidden from non-financial roles to keep raw
  // SAR figures restricted. Percentages and HealthKpiBar growth remain visible.
  const _role = (p.role || "").toLowerCase();
  const canSeeMoney = ["admin", "ceo", "operations", "finance"].includes(_role);
  const canSeeCampaigns = ["admin", "ceo", "marketing", "operations"].includes(_role);
  const canSeeAmbassadors = ["admin", "ceo", "marketing"].includes(_role);
  const canSeeErrors = ["admin", "ceo", "operations", "marketing"].includes(_role);
  const canSeeQuality = ["admin", "ceo", "operations", "marketing"].includes(_role);
  const canSeeEvents = ["admin", "ceo", "marketing", "operations"].includes(_role);

  // ── v119.195: unified role-aware home — pills switch between the AI
  // overview and the role's own dashboard (merged, no separate pages) ──
  const pills = hasDash ? (
    <div className="home-view-pills" data-testid="home-view-pills">
      <button type="button"
              className={`hv-pill ${view === "overview" ? "on" : ""}`}
              data-testid="home-pill-overview"
              onClick={() => setView("overview")}>
        🏠 نظرة عامة
      </button>
      {!isAdmin && (
        <button type="button"
                className={`hv-pill ${view === "dash" ? "on" : ""}`}
                data-testid="home-pill-dash"
                onClick={() => setView("dash")}>
          {ROLE_DASH_LABEL[role] || "🎯 لوحتي"}
        </button>
      )}
      {isAdmin && ADMIN_DASH_OPTIONS.map((o) => (
        <button key={o.key} type="button"
                className={`hv-pill ${view === "dash" && adminDash === o.key ? "on" : ""}`}
                data-testid={`home-pill-${o.key}`}
                onClick={() => { setAdminDash(o.key); setView("dash"); }}>
          {o.label}
        </button>
      ))}
    </div>
  ) : null;

  if (view === "dash" && hasDash) {
    const dashEl = isAdmin && renderRoleDash ? renderRoleDash(adminDash) : roleDash;
    return (
      <div data-testid="home-page" style={{ maxWidth: 1280, margin: "0 auto" }}>
        {pills}
        <React.Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}>
          {dashEl}
        </React.Suspense>
      </div>
    );
  }

  return (
    <div data-testid="home-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {pills}
      {/* v198 — Compact hero: greeting + role/name + refresh indicator on
          one line. Daily tip below is collapsed by default with a persistent
          per-user localStorage flag. Combined header + hero must stay ≤ 35%
          of viewport height on 390px. */}
      <div className="tw-hero tw-hero-compact tw-anim-scale" data-testid="home-hero"
           style={{ marginBottom: 12 }}>
        <div className="tw-hero-deco" aria-hidden />
        <div className="tw-hero-row" style={{ color: "#fff" }}>
          <div className="tw-hero-greet">
            <span className="tw-hero-emoji" aria-hidden>{theme.emoji}</span>
            <span className="tw-hero-name" data-testid="hero-name">
              أهلاً، {p.first_name || p.username}
            </span>
            {p.role_label && (
              <span className="tw-hero-role" data-testid="hero-role">
                {" · "}{p.role_label}
              </span>
            )}
          </div>
          {data?.generated_at && (
            <div className="tw-hero-refresh" data-testid="hero-refresh">
              <span className="tw-live-dot" aria-hidden />
              {refreshing ? "⏳ يحدّث" : <>محدّث · {formatKSADate(new Date(data.generated_at), { hour: "2-digit", minute: "2-digit" })}</>}
            </div>
          )}
        </div>
      </div>

      {/* v119.201 — Notifications moved EXCLUSIVELY to the header bell icon. */}

      {/* 3. Company Health — collapsible v119.160 */}
      <HomeFold id="company-health" icon="💚" title="صحة الشركة"
        summary="مؤشرات الأداء الرئيسية · افتح للتفاصيل"
        defaultOpen={false}>
        <HealthKpiBar />
      </HomeFold>

      {/* 4. System / Integration Health — collapsible v119.160 */}
      <HomeFold id="integration-health" icon="🔌" title="صحة الأنظمة والتكاملات"
        summary="RETM · WhatsApp · Make · Google Drive · Claude"
        defaultOpen={false}>
        <IntegrationHealthCard onNavigate={onNavigate} />
      </HomeFold>

      {/* 5. Meetings */}
      <HomeFold id="meetings" icon="📅" title="الاجتماعات"
        summary={b.upcoming_meeting?.title
          || (b.meetings_summary?.next_meeting?.scheduled_at
                ? `الموعد: ${b.meetings_summary.next_meeting.scheduled_at}`
                : "افتح لرؤية الاجتماعات القادمة والسابقة")}
        defaultOpen={false}>
        <UpcomingMeetingBanner onNavigate={onNavigate} />
        <MeetingSummaryCard summary={b.meetings_summary} onNavigate={onNavigate} />
      </HomeFold>

      {/* 6. My Tasks */}
      <HomeFold id="my-tasks" icon="✅" title="مهامي"
        summary="مهامك من الاجتماعات والأخطاء والفعاليات — افتح للاطلاع على القائمة"
        defaultOpen={false}>
        <MyTasks onNavigate={onNavigate} />
      </HomeFold>

      {/* 7. AI Briefing (collapsible v119.160) */}
      {data?.brief ? (
        <HomeFold id="ai-briefing" icon="🧠" title="Briefing مخصّص من Claude"
          summary={data.brief.slice(0, 90) + (data.brief.length > 90 ? "…" : "")}
          defaultOpen={false}>
          <div className="card vibrant-hero" data-testid="home-brief" style={{ padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: "var(--muted)" }}>
                {briefLoading ? "🔄 جارٍ التحديث..." : (data.cached && "📦 من الذاكرة المؤقتة")}
              </span>
            </div>
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.95, fontSize: 13.8, color: "var(--brown-deep)" }}>
              {data.brief}
            </div>
          </div>
        </HomeFold>
      ) : briefLoading ? (
        <div className="card" data-testid="home-brief-loading" style={{ marginBottom: 16, padding: 18, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "var(--brown-deep)" }}>🧠 يحضّر Claude تحليلك الشخصي...</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>(ثواني معدودة)</div>
        </div>
      ) : null}
    </div>
  );
}


function ShortcutBtn({ label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="btn btn-soft"
            style={{ textAlign: "start", padding: "10px 12px", fontSize: 13, justifyContent: "flex-start" }}>
      {label} ←
    </button>
  );
}

function FingerprintTile({ label, value, highlight }) {
  return (
    <div style={{
      padding: 10,
      background: highlight ? "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)" : "rgba(255,255,255,0.65)",
      color: highlight ? "#fff" : "var(--brown-deep)",
      borderRadius: 6,
      border: highlight ? "none" : "1px solid var(--border)",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 10, opacity: highlight ? 0.92 : 1, color: highlight ? "#fff" : "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}


// ─── Meeting Summary Card ────────────────────────────────────────────────
function MeetingSummaryCard({ summary, onNavigate }) {
  if (!summary) return null;
  const { next_meeting, overdue_count = 0, current_count = 0, overdue_tasks = [], current_tasks = [] } = summary;
  if (!next_meeting && overdue_count === 0 && current_count === 0) return null;
  return (
    <div className="card" data-testid="home-meeting-card" style={{
      marginBottom: 16, padding: 14,
      background: overdue_count > 0
        ? "linear-gradient(135deg, rgba(220,38,38,0.06) 0%, rgba(245,158,11,0.04) 100%)"
        : "linear-gradient(135deg, rgba(20,184,166,0.06) 0%, rgba(124,58,237,0.04) 100%)",
      borderInlineStart: `3px solid ${overdue_count > 0 ? "#DC2626" : "#0EA5E9"}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>🗓 الاجتماع الأسبوعي والمهام</h4>
        <button onClick={() => onNavigate?.("meet")} style={{
          background: "none", border: "1px solid var(--border)", borderRadius: 4,
          padding: "3px 10px", cursor: "pointer", fontSize: 11, color: "var(--brown-deep)",
        }}>فتح الاجتماعات ←</button>
      </div>
      <div data-testid="home-meeting-legacy-warning" style={{
        fontSize: 11, color: "#B45309", background: "#FEF3C7",
        border: "1px solid #FDE68A", borderRadius: 4,
        padding: "6px 10px", marginBottom: 10, lineHeight: 1.6,
      }}>
        ⚠️ مصدر قديم — قيد الدمج، لا يُعتمد. الأرقام أدناه من جدول `db.meetings` القديم
        وقد لا تعكس نظام الاجتماعات الحديث.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 12 }}>
        {next_meeting && (
          <div style={{ padding: 10, background: "rgba(14,165,233,0.08)", borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: "var(--muted)" }}>📅 الاجتماع القادم</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0284C7", marginTop: 2 }}>{next_meeting.title || "اجتماع"}</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>{next_meeting.date}{next_meeting.time ? ` · ${next_meeting.time}` : ""}</div>
          </div>
        )}
        <div style={{ padding: 10, background: overdue_count > 0 ? "rgba(220,38,38,0.08)" : "rgba(0,0,0,0.03)", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "var(--muted)" }}>⚠️ مهام متأخرة</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: overdue_count > 0 ? "#B91C1C" : "var(--muted)" }}>{overdue_count}</div>
        </div>
        <div style={{ padding: 10, background: "rgba(124,58,237,0.05)", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "var(--muted)" }}>🔵 مهام حالية</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#7C3AED" }}>{current_count}</div>
        </div>
      </div>

      {(overdue_tasks.length > 0 || current_tasks.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: overdue_tasks.length > 0 && current_tasks.length > 0 ? "1fr 1fr" : "1fr", gap: 12 }}>
          {overdue_tasks.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#B91C1C", marginBottom: 4 }}>متأخرة الآن</div>
              {overdue_tasks.map((t, i) => (
                <div key={i} style={{ padding: "4px 8px", fontSize: 11.5, background: "rgba(220,38,38,0.05)", borderRadius: 4, marginBottom: 3, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600, color: "var(--brown-deep)" }}>{t.title}</div>
                  <div style={{ color: "var(--muted)", fontSize: 10 }}>{t.owner} · مستحقة {t.due || "—"}</div>
                </div>
              ))}
            </div>
          )}
          {current_tasks.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", marginBottom: 4 }}>حالية</div>
              {current_tasks.map((t, i) => (
                <div key={i} style={{ padding: "4px 8px", fontSize: 11.5, background: "rgba(124,58,237,0.04)", borderRadius: 4, marginBottom: 3, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600, color: "var(--brown-deep)" }}>{t.title}</div>
                  <div style={{ color: "var(--muted)", fontSize: 10 }}>{t.owner} · {t.due || "بدون موعد"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Ambassadors Home Card ───────────────────────────────────────────────
function AmbassadorsHomeCard({ summary, onNavigate }) {
  if (!summary || summary.total === 0) return null;
  return (
    <div className="card" data-testid="home-ambassadors-card" style={{
      marginBottom: 16, padding: 14,
      background: "linear-gradient(135deg, rgba(124,58,237,0.05) 0%, rgba(236,72,153,0.04) 100%)",
      borderInlineStart: "3px solid #7C3AED",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>🌟 شركاء ثواني</h4>
        <button onClick={() => onNavigate?.("amb")} style={{
          background: "none", border: "1px solid var(--border)", borderRadius: 4,
          padding: "3px 10px", cursor: "pointer", fontSize: 11, color: "var(--brown-deep)",
        }}>إدارة الشركاء ←</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: summary.top?.length > 0 ? 12 : 0 }}>
        <HKpi label="إجمالي" value={fm(summary.total)} sub={`${fm(summary.active)} نشط`} color="#7C3AED" />
        <HKpi label="استخدامات" value={fm(summary.total_uses)} color="#10B981" />
        <HKpi label="إيراد" value={`${fm(summary.total_revenue)} ر`} color="#0284C7" />
        <HKpi label="عمولة مستحقة" value={`${fm(summary.commission_balance)} ر`} color="#D97706" />
        {summary.idle > 0 && <HKpi label="خاملون" value={fm(summary.idle)} color="#6B7280" />}
      </div>
      {summary.top?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6, fontWeight: 700 }}>🏆 أعلى الشركاء أداءً</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {summary.top.slice(0, 5).map((a, i) => (
              <span key={a.code} style={{
                background: "rgba(124,58,237,0.10)", color: "var(--brown-deep)",
                padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600,
              }}>
                {i === 0 && "🥇"}{i === 1 && "🥈"}{i === 2 && "🥉"} {a.name} · {fm(a.uses)} استخدام
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Campaigns Home Card ─────────────────────────────────────────────────
function CampaignsHomeCard({ summary, activeCamps, onNavigate }) {
  const hasData = summary?.total_campaigns > 0 || activeCamps?.length > 0;
  if (!hasData) return null;
  return (
    <div className="card" data-testid="home-campaigns-card" style={{
      marginBottom: 16, padding: 14,
      background: "linear-gradient(135deg, rgba(236,72,153,0.05) 0%, rgba(245,158,11,0.04) 100%)",
      borderInlineStart: "3px solid #EC4899",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>🎯 الحملات وأثرها</h4>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => onNavigate?.("camps")} style={{
            background: "none", border: "1px solid var(--border)", borderRadius: 4,
            padding: "3px 10px", cursor: "pointer", fontSize: 11, color: "var(--brown-deep)",
          }}>الحملات ←</button>
          <button onClick={() => onNavigate?.("impact")} style={{
            background: "#EC4899", color: "#fff", border: "none", borderRadius: 4,
            padding: "3px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600,
          }}>📊 قياس الأثر ←</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 10 }}>
        <HKpi label="إجمالي الحملات" value={fm(summary?.total_campaigns ?? 0)} color="#EC4899" />
        <HKpi label="نشطة الآن" value={fm(summary?.active_campaigns ?? 0)} color="#10B981" />
      </div>
      {activeCamps?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6, fontWeight: 700 }}>🚀 الحملات النشطة</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {activeCamps.slice(0, 5).map((c, i) => (
              <span key={i} style={{
                background: "rgba(236,72,153,0.10)", color: "var(--brown-deep)",
                padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600,
              }}>
                {c.name} {c.end_date && `· حتى ${c.end_date}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HKpi({ label, value, sub, color }) {
  return (
    <div style={{ padding: 8, background: "rgba(255,255,255,0.5)", borderRadius: 6 }}>
      <div style={{ fontSize: 10, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: color || "var(--brown-deep)", marginTop: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// 📅 Upcoming approved CEO meeting — shows the next/most-recent approved
// weekly meeting so the whole team knows when to gather. Renders nothing
// if there is no approved meeting yet, so it stays out of the way until
// the CEO actually publishes one.
function UpcomingMeetingBanner({ onNavigate }) {
  const [m, setM] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get("/ceo/meetings/upcoming-public")
       .then(({ data }) => { if (alive) setM(data?.meeting || null); })
       .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!m) return null;
  let when = "—", isPast = false;
  try {
    const d = new Date(m.scheduled_at);
    when = formatKSADateTime(d, { weekday: "long", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    isPast = d < new Date();
  } catch { /* keep defaults */ }
  return (
    <div className="card" data-testid="home-meeting-banner" style={{
      padding: "12px 16px", marginBottom: 12,
      background: isPast
        ? "linear-gradient(135deg, rgba(100,116,139,0.12) 0%, transparent 100%)"
        : "linear-gradient(135deg, rgba(184,134,11,0.15) 0%, rgba(107,68,35,0.10) 100%)",
      borderInlineStart: `4px solid ${isPast ? "#64748b" : "#b8860b"}`,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
    }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: 13.5, color: "var(--brown-deep)" }}>
          📅 {m.title}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
          {isPast ? "آخر اجتماع:" : "🔔 جهّز نفسك —"} {when} · {m.task_count || 0} مهمة
        </div>
      </div>
      <button type="button" data-testid="home-meeting-link"
        onClick={() => onNavigate?.("meet")}
        style={{ padding: "6px 14px", borderRadius: 999, background: "#6b4423", color: "#fff8e6", border: "none", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
        افتح الأجندة
      </button>
    </div>
  );
}

